import type { Category, CategoryFlag, StoredCategory, StoredTaxonomy, Taxonomy } from './types'

/** Nothing is seeded. Flags are whatever the user decides they need. */
export const DEFAULT_FLAGS: CategoryFlag[] = []

export function newFlagId(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 24) || 'flag'
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
}

/**
 * Flags are the only labels a category carries, and they are entirely the user's.
 * "Counts as implementation" and "off the clock" are gone as built-in properties:
 * anything the app used to derive from them is derived per flag instead, so
 * nothing has to be told what a category means.
 *
 * Archived survives as a property because it is a lifecycle, not a label — a
 * retired category is not describing the work, it is out of use.
 */
export function normaliseTaxonomy(raw: StoredTaxonomy | Taxonomy | null | undefined): Taxonomy {
  const bindings = (raw as StoredTaxonomy | null | undefined)?.bindings
  const bound = new Set(Object.values(bindings ?? {}).filter((id): id is string => Boolean(id)))

  const live = (raw?.flags ?? DEFAULT_FLAGS).filter(
    (flag) => flag && flag.id && !bound.has(flag.id),
  )
  // A flag used to be able to say its categories do not count. The categories
  // say it themselves now, so the old answer is read once here and carried onto
  // them; the flag goes back to being nothing but a label.
  const excluding = new Set(
    live.filter((flag) => flag.excludeFromInsights === true).map((flag) => flag.id),
  )
  const flags = live.map((flag) => ({ id: flag.id, label: flag.label }))
  const ids = new Set(flags.map((flag) => flag.id))

  const categories: Category[] = (raw?.categories ?? []).map((stored) => {
    const carried = Array.isArray(stored.flags) ? stored.flags : []
    const source = stored as StoredCategory
    return {
      id: source.id,
      label: source.label,
      shortLabel: source.shortLabel,
      color: source.color,
      flags: carried.filter((id) => ids.has(id)),
      archived: bindings
        ? hasBound(carried, bindings['archived'])
        : source.archived === true,
      excludeFromInsights:
        source.excludeFromInsights === true || carried.some((id) => excluding.has(id)),
    }
  })

  return { categories, layout: raw?.layout ?? [], flags }
}

function hasBound(carried: string[], flagId: string | null | undefined): boolean {
  return Boolean(flagId) && carried.includes(flagId as string)
}

export function toStoredTaxonomy(taxonomy: Taxonomy): StoredTaxonomy {
  return {
    categories: taxonomy.categories.map((category) => ({
      id: category.id,
      label: category.label,
      shortLabel: category.shortLabel,
      color: category.color,
      flags: category.flags,
      archived: category.archived,
      excludeFromInsights: category.excludeFromInsights,
    })),
    layout: taxonomy.layout,
    flags: taxonomy.flags,
  }
}

/**
 * The categories a typed query matches, in palette order.
 *
 * Every whitespace-separated term has to appear somewhere in the label or the
 * short one, so "man work" finds "Manual work" without either word having to be
 * spelled out in full, and "work" alone still finds every kind of it. An empty
 * query is not a filter that matches nothing: it is no filter at all.
 */
export function matchCategories(palette: Category[], query: string): Category[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return palette
  return palette.filter((category) => {
    const haystack = `${category.label} ${category.shortLabel}`.toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

export function toggleFlag(category: Category, flagId: string): Category {
  return {
    ...category,
    flags: category.flags.includes(flagId)
      ? category.flags.filter((id) => id !== flagId)
      : [...category.flags, flagId],
  }
}

/**
 * Why a taxonomy is not ready to be written, or null when it is. Autosave holds
 * on these rather than failing: a name cleared halfway through a rename is not a
 * mistake yet, it is a rename in progress.
 */
export function taxonomyProblem(draft: Taxonomy): string | null {
  if (draft.categories.some((category) => !category.label.trim())) {
    return 'Every category needs a name.'
  }
  if (draft.categories.some((category) => !category.shortLabel.trim())) {
    return 'Every category needs a button label.'
  }
  if (draft.flags.some((flag) => !flag.label.trim())) return 'Every flag needs a name.'
  return null
}
